import { Link } from "react-router-dom";
import { AlertOctagon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { UserAvatar } from "@/components/UserAvatar";
import { cn } from "@/lib/utils";
import { AccountabilityBlockerEntry } from "@/types";
import { getPriorityStatusMeta } from "@/components/priorities/priorityCardTokens";

interface BlockingSectionProps {
  entries: AccountabilityBlockerEntry[];
  isLoading: boolean;
}

/** People who are blocking others, and the specific tasks + owners waiting on them. */
export function BlockingSection({ entries, isLoading }: BlockingSectionProps) {
  if (isLoading) {
    return <div className="h-40 animate-pulse rounded-xl border border-border bg-muted/30" />;
  }

  if (entries.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border/60 bg-muted/20 p-8 text-center">
        <p className="text-sm font-medium text-muted-foreground">Nobody is currently blocking anyone</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {entries.map(({ user, blockedCount, blockedTasks }) => (
        <div key={user.pid} className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center gap-3">
            <UserAvatar src={user.avatarUrl} name={user.fullName} className="h-9 w-9" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{user.fullName}</p>
              {user.position && <p className="truncate text-xs text-muted-foreground">{user.position}</p>}
            </div>
            <Badge variant="outline" className="gap-1.5 shrink-0 border-destructive/30 text-destructive">
              <AlertOctagon className="h-3.5 w-3.5" />
              Blocking {blockedCount}
            </Badge>
          </div>

          <ul className="mt-3 flex flex-col gap-2 border-t border-border pt-3">
            {blockedTasks.map((task) => {
              const meta = getPriorityStatusMeta(task.status);
              return (
                <li key={task.pid} className="text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <Link to={`/priorities/${task.pid}`} className="min-w-0 flex-1 truncate hover:underline">
                      <span className="mr-1.5 font-mono text-xs text-muted-foreground">{task.code}</span>
                      {task.name}
                    </Link>
                    <Badge variant="outline" className={cn("shrink-0 gap-1", meta.className)}>
                      <meta.icon className="h-3 w-3" />
                      {meta.label}
                    </Badge>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    Owner: {task.owner.fullName}
                    {task.blockedReason ? ` — ${task.blockedReason}` : ""}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
